import React, { useEffect, useState, useContext, useMemo } from 'react';
import { BrowserRouter as Router, Switch, Route, useParams } from "react-router-dom";
import FormColumn from "components/Common/FormColumn";
import moment from 'moment';
import DataService from "infrastructure/Services/DataService";
import ApiService from "infrastructure/Services/ApiService";
import { Card, CardHeader, CardBody, NavItem, NavLink, Nav, Container, Row, Col, Button, Input } from "reactstrap";
import Loader from 'components/Common/Loader';
import UserContext from "context/UserContext";
import StorageService from 'infrastructure/StorageService';
import Constants from 'infrastructure/Constants';
import { useDropzone } from "react-dropzone";
import { toast } from 'react-toastify';
export default ((props) => {
  const [booking, setBooking] = useState();
  const [technicianNotes, setTechnicianNotes] = useState();
  const { id } = useParams();
  const user = useContext(UserContext);
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: files => updateFiles(files)
  });
  const style = useMemo(
    () => ({
      ...styles.baseStyle,
      ...(isDragActive ? styles.activeStyle : {}),
      ...(isDragAccept ? styles.acceptStyle : {}),
      ...(isDragReject ? styles.rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const updateFiles = async (files) => {
    const _files = [];
    for (const f of files) {
      var content = await toBase64(f);
      _files.push({
        mimeType: f.type,
        name: f.name,
        file: content
      })
    }


    ApiService.post(`savedbooking/lab/${id}/attachments`, { attachments: _files })
      .then((response) => {
        setBooking(response);
      });
  }

  const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });


  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    const tempPeriods = await DataService.periods();
    const campuses = await DataService.labBookings.campuses();

    ApiService.get(`savedbooking/lab/${id}`)
      .then(resp => {


        setBooking(resp);
      })
  }


  const getAttachment = (attachmentId) => {
    ApiService.get(`booking/lab/2000-01-01/${id}/attachment/${attachmentId}`)
      .then(resp => {
        if (resp && resp.url) {
          window.open(resp.url, '_newtab');
        }
      });
  }

  const normalizePath = (pathValue) => (pathValue || "").replace(/\\/g, "/").replace(/^\/+/, "");

  const getChemicalSdsUrl = (chemical) => {
    const user = StorageService.get(StorageService.keys.user);
    const remotePath = normalizePath(chemical?.path || chemical?.Path);

    if (!user?.organisationId || !user?.sasToken || !remotePath) {
      return null;
    }

    return `${Constants.api.storageUrl}${user.organisationId}/${remotePath}?${user.sasToken}`;
  };

  const downloadChemicalSds = (chemical) => {
    const url = getChemicalSdsUrl(chemical);

    if (!url) {
      toast.error("Unable to resolve the SDS download link for this chemical.");
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const generateMiniSdsPack = async () => {
    const chemicals = booking?.chemicals || [];

    if (!chemicals.length) {
      toast.info("There are no chemicals on this booking to include in the Mini SDS Pack.");
      return;
    }

    try {
      const { PDFDocument, rgb } = await import('pdf-lib');
      const doc = await PDFDocument.create();
      const font = await doc.embedFont('Helvetica');
      const textColor = rgb(0.07, 0.09, 0.22);
      const pageWidth = 595;
      const pageHeight = 842;

      const addText = (page, text, x, y, size = 12) => {
        page.drawText(text, { x, y, size, font, color: textColor, maxWidth: pageWidth - 2 * x });
      };

      const coverPage = doc.addPage([pageWidth, pageHeight]);
      addText(coverPage, 'Mini SDS Pack', 72, 760, 24);
      addText(coverPage, booking.practicalName || 'Practical', 72, 700, 18);
      addText(coverPage, `Date: ${booking.bookingDate ? new moment(booking.bookingDate).format('dddd, LL') : 'N/A'}`, 72, 660, 14);
      addText(coverPage, `Teacher: ${booking.createdByUserName || 'N/A'}`, 72, 620, 14);
      addText(coverPage, `Room: ${booking.room || 'N/A'}`, 72, 580, 14);
      addText(coverPage, `Class: ${booking.class || 'N/A'}`, 72, 540, 14);

      const tocPage = doc.addPage([pageWidth, pageHeight]);
      addText(tocPage, 'Table of Contents', 72, 780, 18);
      let tocY = 730;
      chemicals.forEach((chemical, index) => {
        tocY -= 26;
        tocPage.drawText(`${index + 1}. ${chemical.documentName || chemical.DocumentName || chemical.path || chemical.Path}`, {
          x: 72,
          y: tocY,
          size: 12,
          font,
          color: textColor,
          maxWidth: pageWidth - 144,
        });
      });

      for (const chemical of chemicals) {
        const url = getChemicalSdsUrl(chemical);
        if (!url) continue;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`Unable to download ${chemical.documentName || chemical.DocumentName || 'the selected SDS PDF'}.`);

        const pdfBytes = await response.arrayBuffer();
        const sourcePdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await doc.copyPages(sourcePdf, sourcePdf.getPageIndices());
        copiedPages.forEach((page) => doc.addPage(page));
      }

      const pdfBytes = await doc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl;
      downloadLink.download = `${(booking.practicalName || 'mini-sds-pack').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
      downloadLink.click();
      window.URL.revokeObjectURL(fileUrl);
      toast.success('Mini SDS Pack generated successfully.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Unable to generate the Mini SDS Pack.');
    }
  };

  const deleteAttachment = (attachmentId) => {
    const confirm = window.confirm("Are you sure you want to delete this attachment?");
    if (!confirm) {
      return;
    }
    ApiService.delete(`savedbooking/lab/${id}/attachment/${attachmentId}`)
      .then(resp => {
        if (resp) {
          setBooking(prevBooking => ({
            ...prevBooking,
            attachments: prevBooking.attachments.filter(att => att.id !== attachmentId)
          }));
        }
      });
  }

  return <Container className="mt--7" fluid>
    <Row>
      <Col className="mb-5 mb-xl-0" xl="12">
        <Card className="shadow">
          <CardBody>
            {!booking && <Row><Col xs={12} className="text-center"><Loader /></Col></Row>}
            {booking && <>
              <Row className={`d-flex align-items-center`}>
                <Col xs={12} md={6}>
                  <h6 className="text-uppercase ls-1 mb-0">Date</h6>
                  <h3>{new moment(booking.bookingDate).format("dddd, LL")}</h3>
                </Col>
                <Col xs={12} md={6}>
                  <h6 className="text-uppercase ls-1 mb-0">Time</h6>
                  <h3>
                    {booking.startPeriodName}
                    {booking.endPeriodName &&
                      booking.endPeriodName != booking.startPeriodName && (
                        <> - {booking.endPeriodName}</>
                      )}
                  </h3>
                </Col>
              </Row>
              <Row>
                <FormColumn label={`User`}>
                  <h4>{booking.createdByUserName}</h4>
                </FormColumn>
                <FormColumn label={`Created On`}>
                  <h4>{new moment(booking.createdDate).format("dddd, LL hh:mm:ss A")}</h4>
                </FormColumn>


                <FormColumn label={`Year Level`}>
                  <h4>{booking.createdForYearLevel}</h4>
                </FormColumn>
              </Row>
              <Row>
                <FormColumn label={`Practical Name`} columns={2}>
                  <h4>{booking.practicalName}</h4>
                </FormColumn>

                <FormColumn label={`Book Reference`} columns={2}>
                  <h4>{booking.bookReference}</h4>
                </FormColumn>
              </Row>
              <Row>
                <FormColumn label={`Technician Assistance Required`} columns={2}>
                  <h4>{booking.technicianAssistanceRequired == true ? 'Yes' : 'No'}</h4>
                </FormColumn>
              </Row>
              <Row>
                <FormColumn label={`Booking Notes`} columns={1}>
                  <h4>{booking.bookingNotes}</h4>
                </FormColumn>
              </Row>

              {(booking.chemicals || []).length > 0 && <>
                <hr className={`my-3`} />
                <Row>
                  <Col xs={12}>
                    <h3>Chemicals in use</h3>
                    <ul className="mb-0">
                      {(booking.chemicals || []).map((chemical) => (
                        <li key={chemical.id || chemical.DocumentName || chemical.documentName || chemical.path || chemical.Path}>
                          {chemical.documentName || chemical.DocumentName || chemical.path || chemical.Path}
                          {chemical.vendor || chemical.Vendor ? ` — ${chemical.vendor || chemical.Vendor}` : ""}
                          <Button color="link" size="sm" className="ml-2 p-0 align-baseline" onClick={() => downloadChemicalSds(chemical)}>Download SDS</Button>
                        </li>
                      ))}
                    </ul>
                    <Button color="primary" size="sm" className="mt-3" onClick={generateMiniSdsPack}>Generate Mini SDS Pack</Button>
                  </Col>
                </Row>
              </>}
              {booking.attachments && booking.attachments.length > 0 && <>
                <hr className={`my-3`} />
                <Row className={`d-flex align-items-center`}>
                  <Col xs={12} md={6}>
                    <h3>Attachments</h3>
                    {booking.attachments.map(attachment => {
                      return <>
                        <p><a href={`#`} onClick={(e) => { e.preventDefault(); getAttachment(attachment.id); }}>{attachment.name}</a>
                          {user.isAdmin && <> &nbsp; <a className="text-danger cursor-pointer" onClick={() => deleteAttachment(attachment.id)}><i className={`fa fa-times`}></i></a></>}
                        </p>
                      </>
                    })}
                  </Col>
                </Row></>
              }
              {user.isAdmin &&
                <Row className={`d-flex align-items-center`}>
                  <Col xs={12} md={6}>
                    <h3>Add Attachment</h3>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p className={`mb-0`}>
                        Drag and drop some files here, or click to select files
                      </p>
                    </div>
                  </Col>
                </Row>
              }

              {booking.risks.map(risk => {
                return <>
                  <hr className={`my-3`} />
                  <Row className={`d-flex align-items-center`}>
                    <Col xs={12}>
                      <h3>{risk.name} Risks</h3>
                    </Col>
                  </Row>
                  {risk.hazards.map((hazard) => (
                    <>
                      <Row className="d-flex align-items-center mb-2">
                        <Col xs={6}>
                          {hazard.name}
                        </Col>
                        <Col xs={6}>
                          {hazard.value}
                        </Col>
                      </Row>
                    </>))}


                  <Row className="d-flex align-items-center mt-4">
                    <Col xs={6}>
                      <h4>Notes</h4>
                    </Col>
                    <Col xs={6}>
                      {risk.notes}
                    </Col>
                  </Row>
                </>
              })}
              <hr className={`my-3`} />
              <Row className="d-flex align-items-center my-2">
                <Col xs={6}>
                  <h4>Control Measures</h4>
                </Col>
              </Row>
              <Row className="d-flex align-items-center mb-2">
                <Col xs={4} className='bold'>
                  <b>Control Measure</b>
                </Col>

                <Col xs={4}>
                  <b>Mitigation</b>
                </Col>
                <Col xs={4}>
                  <b>Other</b>
                </Col>
              </Row>
              {booking.controlMeasures.map((method) => (<Row key={method.name} className="d-flex align-items-center mb-2">
                <Col xs={4}>
                  {method.name}
                </Col>

                <Col xs={4}>
                  {method.mitigations && method.mitigations.filter(m => m !== 'Other').concat(method.mitigations.includes('Other') ? ['Other'] : []).map((m, i) => <div key={i}>{m}</div>)}
                </Col>
                <Col xs={4}>
                  {method.mitigationOther}
                </Col>
              </Row>
              ))}
              {user.isAdmin && <Row>
                <FormColumn label={`Notes`} columns={1}>
                  <h4>{booking.notes}</h4>
                </FormColumn>
              </Row>}
              <hr className={`my-3`} />
            </>}

          </CardBody>
        </Card>
      </Col>
    </Row>
  </Container>;

});

const styles = {

  baseStyle: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    marginTop: 20,
    marginBottom: 20,
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
  activeStyle: {
    borderColor: "#2196f3",
  },
  acceptStyle: {
    borderColor: "#00e676",
  },

  rejectStyle: {
    borderColor: "#ff1744"
  }
}
